import { VStack, HStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Text, Box } from 'native-base';
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue, runOnJS } from 'react-native-reanimated';

import { LabelTypes, labelTypesFormatted } from './constants';

export declare namespace MultiSliderTypes {
  interface RangeProps {
    min: number;
    max: number;
  }

  type ValueChange = (range: RangeProps, fieldName: string, filterIndex: number) => void;

  interface Props {
    type: 'multiSlider';
    min: number;
    max: number;
    step: number;
    title: string;
    fieldName: string;
    filterIndex: number;
    onValueChange: ValueChange;
    labelType: LabelTypes;
    selection?: RangeProps;
  }
}

const DEFAULT_STYLE_LINE = {
  height: 4,
  marginTop: -4,
  borderRadius: 999,
};

const MIN_DISTANCE_BETWEEN_MARKERS = 10;

export const hasMultiSliderSelection = (
  selection: MultiSliderTypes.RangeProps,
  filter: MultiSliderTypes.Props
) =>
  (filter.min !== Number(selection.min) && !!selection.min) ||
  (filter.max !== Number(selection.max) && !!selection.max);

export const MultiSlider: React.FC<MultiSliderTypes.Props> = ({
  max,
  min,
  onValueChange,
  step,
  title,
  labelType,
  fieldName,
  filterIndex,
  selection,
}) => {
  const [width, setWidth] = useState(0);
  const MARKER_SIZE = 16;
  const MAX_WIDTH = width - MARKER_SIZE / 2 + 6;

  const markerLeftStartX = useRef<number>(0);
  const markerRightStartX = useRef<number>(0);
  const markerLeft = useSharedValue(0);
  const scaleMarkerLeft = useSharedValue(1);
  const markerRight = useSharedValue(width);
  const scaleMarkerRight = useSharedValue(1);

  useEffect(() => {
    markerLeft.value = selection?.min ? (selection.min / (max - min)) * width : 0;
    markerRight.value = selection?.max ? (selection.max / (max - min)) * width : width;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const [range, setRange] = useState(selection?.max ? selection : { min, max });

  const roundValue = (value: number) =>
    Math.round((min + (value / MAX_WIDTH) * (max - min)) / step) * step;

  const gestureHandlerLeftMarker = Gesture.Pan()
    .onStart(() => {
      markerLeftStartX.current = markerLeft.value;
    })
    .onChange((event) => {
      scaleMarkerLeft.value = 1.3;
      const startX = markerLeftStartX.current;
      const translationX = event.translationX;
      const markerRightValue = markerRight.value;
      if (startX + translationX < 0) {
        markerLeft.value = 0;
      } else if (startX + translationX > markerRightValue - MIN_DISTANCE_BETWEEN_MARKERS) {
        markerLeft.value = markerRightValue - MIN_DISTANCE_BETWEEN_MARKERS;
      } else {
        markerLeft.value = startX + translationX;
      }
      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      setRange({ min: minValue, max: maxValue });
    })
    .onFinalize(() => {
      scaleMarkerLeft.value = 1;
      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      runOnJS(onValueChange)({ min: minValue, max: maxValue }, fieldName, filterIndex);
    });

  const gestureHandlerRightMarker = Gesture.Pan()
    .onStart(() => {
      markerRightStartX.current = markerRight.value;
    })
    .onChange((event) => {
      scaleMarkerRight.value = 1.3;

      const startX = markerRightStartX.current;
      const translationX = event.translationX;
      const markerLeftValue = markerLeft.value;

      if (startX + translationX > MAX_WIDTH) {
        markerRight.value = MAX_WIDTH;
      } else if (startX + translationX < markerLeftValue + MIN_DISTANCE_BETWEEN_MARKERS) {
        markerRight.value = markerLeftValue + MIN_DISTANCE_BETWEEN_MARKERS;
      } else {
        markerRight.value = startX + translationX;
      }

      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      setRange({ min: minValue, max: maxValue });
    })
    .onFinalize(() => {
      scaleMarkerRight.value = 1;

      const minValue = roundValue(markerLeft.value);
      const maxValue = roundValue(markerRight.value);
      runOnJS(onValueChange)({ min: minValue, max: maxValue }, fieldName, filterIndex);
    });

  const styles = StyleSheet.create({
    marker: {
      position: 'absolute',
      height: MARKER_SIZE,
      width: MARKER_SIZE,
      borderRadius: MARKER_SIZE / 2,
      backgroundColor: Utils.ColorToken.WithOpacity('brand.tertiary.principal', 0.95),
      marginLeft: -8,
      marginTop: -MARKER_SIZE + 6,
    },
  });

  const styleLine = useAnimatedStyle(() => {
    return {
      ...DEFAULT_STYLE_LINE,
      backgroundColor: Utils.ColorToken.WithOpacity('brand.tertiary.principal', 0.5),
      width: markerRight.value - markerLeft.value,
      transform: [{ translateX: markerLeft.value }],
    };
  });

  const styleBackgroundLine = useAnimatedStyle(() => {
    return {
      ...DEFAULT_STYLE_LINE,
      backgroundColor: '#00000042',
      width,
    };
  });

  const styleAnimatedLeftMarker = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: markerLeft.value }, { scale: scaleMarkerLeft.value }],
    };
  });

  const styleAnimatedRightMarker = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: markerRight.value }, { scale: scaleMarkerRight.value }],
    };
  });

  return (
    <VStack w="full" onLayout={(event) => setWidth(event.nativeEvent.layout.width)}>
      <Text variant="semibold" fontSize={Typography['fontsize.md']} lineHeight="distant">
        {title}
      </Text>
      <Box w={'full'} py={Spacing['inline.xxs']}>
        <Animated.View style={styleBackgroundLine} />
        <Animated.View style={styleLine} />
        <Box>
          <GestureDetector gesture={gestureHandlerLeftMarker}>
            <Animated.View style={[styles.marker, styleAnimatedLeftMarker]} />
          </GestureDetector>
          <GestureDetector gesture={gestureHandlerRightMarker}>
            <Animated.View style={[styles.marker, styleAnimatedRightMarker]} />
          </GestureDetector>
        </Box>
      </Box>
      <HStack w="full" justifyContent={'space-between'}>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {labelTypesFormatted[labelType].min(range.min)}
        </Text>
        <Text variant={'regular'} fontSize={Typography['fontsize.md']} lineHeight={'distant'}>
          {labelTypesFormatted[labelType].max(range.max, max)}
        </Text>
      </HStack>
    </VStack>
  );
};
